<template>
  <el-dialog
    :visible="visible"
    width="780px"
    title="AI 生成自定义脚本"
    :close-on-click-modal="false"
    append-to-body
    @opened="handleOpened"
    @closed="handleClosed"
    @update:visible="$emit('update:visible', $event)"
  >
    <!-- 步骤：输入规则 -->
    <div v-if="step === 'input'">
      <el-form label-width="80px">
        <el-form-item label="脚本名称">
          <el-input
            v-model="scriptName"
            placeholder="输入脚本名称，如「业务模块分类脚本」"
            maxlength="50"
            size="small"
          />
        </el-form-item>
        <el-form-item label="脚本描述">
          <el-input
            v-model="scriptDescription"
            placeholder="简要描述脚本的功能用途（选填）"
            maxlength="200"
            size="small"
          />
        </el-form-item>
        <el-form-item label="规则描述">
          <div style="margin-bottom:8px;font-size:12px;color:#909399;line-height:1.6;">
            <p style="margin:0 0 4px;">用自然语言描述分类规则，AI 将生成 JavaScript 脚本。以下为参考：</p>
            <pre style="margin:0;background:#F5F7FA;padding:8px;border-radius:4px;font-size:12px;color:#606266;white-space:pre-wrap;">
1. 文件名以 Controller 结尾的归为 CONTROLLER
2. 包含 @TableName 注解的归为 ENTITY
3. 文件名以 Mapper 结尾的归为 MAPPER
4. 包含 enum 关键字的归为 ENUM
5. 包名包含 .vo. 或者 .dto. 的归为 VO / DTO</pre>
          </div>
          <el-input
            type="textarea"
            :rows="10"
            v-model="userPrompt"
            placeholder="在此输入自然语言的分类规则...&#10;&#10;示例：&#10;所有文件名以Service结尾且在com.yida.**.service包下的归为SERVICE&#10;所有实现BaseEnum接口的归为ENUM&#10;..."
          />
        </el-form-item>
      </el-form>
    </div>

    <!-- 步骤：预览脚本 -->
    <div v-if="step === 'preview'">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <div>
          <span style="font-weight:500;font-size:14px;">
            <i class="el-icon-document" style="margin-right:4px;color:#409EFF;"></i>
            {{ scriptName || '未命名脚本' }}
          </span>
          <el-tag size="mini" style="margin-left:8px;">JavaScript</el-tag>
        </div>
        <div style="display:flex;gap:6px;">
          <el-button size="mini" @click="step = 'input'">
            <i class="el-icon-edit"></i> 修改提示词
          </el-button>
          <el-button size="mini" @click="regenerateScript" :loading="generating">
            <i class="el-icon-refresh"></i> 重新生成
          </el-button>
        </div>
      </div>
      <div style="border:1px solid #DCDFE6;border-radius:4px;overflow:hidden;">
        <div style="background:#1E1E1E;padding:8px 12px;display:flex;gap:8px;align-items:center;">
          <span style="color:#C0C4CC;font-size:11px;">script.js</span>
          <span style="flex:1;"></span>
          <el-button
            size="mini"
            type="text"
            style="color:#fff;"
            @click="copyScript"
          >
            <i class="el-icon-document-copy"></i> 复制
          </el-button>
        </div>
        <pre
          style="margin:0;font-size:12px;line-height:1.6;max-height:420px;overflow:auto;white-space:pre-wrap;word-break:break-all;background:#1E1E1E;color:#D4D4D4;padding:12px;font-family:Menlo,Consolas,monospace;"
        >{{ generatedContent || '/* AI 正在生成... */' }}</pre>
      </div>
    </div>

    <template #footer>
      <div v-if="step === 'input'">
        <el-button @click="$emit('update:visible', false)">取消</el-button>
        <el-button
          type="primary"
          @click="generateScript"
          :loading="generating"
          :disabled="!scriptName.trim() || !userPrompt.trim()"
        >
          {{ generating ? 'AI 生成中...' : 'AI 生成脚本' }}
        </el-button>
      </div>
      <div v-if="step === 'preview'">
        <el-button @click="handleBackFromPreview">取消</el-button>
        <el-button
          type="primary"
          @click="saveScript"
          :loading="saving"
          :disabled="!generatedContent"
        >
          {{ saving ? '保存中...' : '保存脚本' }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { createScanScript, updateScanScript } from "@/api/dataApi";

const GENERATE_PROMPT = `你是一个 Java 源码扫描脚本生成器。生成一个 JavaScript 函数 classifyFile，用于对 Java 源文件进行智能分类和信息提取。

## 输入参数
\`\`\`js
{
  fileName: 'UserController.java',  // 文件名
  filePath: 'user/src/main/java/com/yida/controller/UserController.java', // 相对路径
  content: '...'  // 文件完整内容
}
\`\`\`

## 返回值（与扫描信息表字段一致，可只返回type，其余可选）
\`\`\`js
{
  type: 'ENTITY',       // 必填: ENUM|ENTITY|VO|DTO|CONTROLLER|SERVICE|SERVICE_IMPL|MAPPER|CONFIG|INTERFACE|OTHER
  code: 'user',         // 业务编码
  description: '用户实体', // 描述
  tableName: 'sys_user', // 表名（仅ENTITY）
  hasParentId: false,    // 树形标志
  isJunction: false,     // 是否关联表
  hasListApi: false,     // 是否有list接口
  listApiPath: '/user/list', // list接口路径
  enumCode: 'userStatus', // 枚举编码
  enumLabel: '用户状态',   // 枚举名称
  apiPaths: [{ method: 'GET', path: '/user/list' }], // API路径
  fields: [{ name: 'userName', type: 'String', description: '用户名' }] // 字段列表
}
\`\`\`

## 约束
- 函数名必须是 classifyFile，使用 export { classifyFile } 导出
- 只返回函数代码，不要额外解释
- type 必须返回有效值，无法确定返回 'OTHER'
- 其余字段可选，不确定的返回 undefined 即可
- 判断基于文件名后缀、包路径、注解等信号
- 可以使用正则表达式匹配

## 用户自定义规则
`;

export default {
  name: 'ScriptGeneratorDialog',
  props: {
    visible: Boolean,
    projectId: { type: Number, default: null },
    /** 编辑已有脚本时传入 */
    editScript: { type: Object, default: null }
  },
  data() {
    return {
      step: 'input',
      scriptName: '',
      scriptDescription: '',
      userPrompt: '',
      generatedContent: '',
      generating: false,
      saving: false
    };
  },
  watch: {
    visible(val) {
      if (val && this.editScript) {
        this.scriptName = this.editScript.name || '';
        this.scriptDescription = this.editScript.description || '';
        this.generatedContent = this.editScript.content || '';
        this.userPrompt = '';
        if (this.generatedContent) {
          this.step = 'preview';
        } else {
          this.step = 'input';
        }
      } else if (val) {
        this.step = 'input';
        this.scriptName = '';
        this.scriptDescription = '';
        this.userPrompt = '';
        this.generatedContent = '';
      }
    }
  },
  methods: {
    handleOpened() {
      // 重置状态在 watch visible 中处理
    },
    handleClosed() {
      this.step = 'input';
      this.generatedContent = '';
    },
    handleBackFromPreview() {
      this.$emit('update:visible', false);
    },

    async generateScript() {
      if (!this.scriptName.trim()) {
        this.$message.warning('请输入脚本名称');
        return;
      }
      if (!this.userPrompt.trim()) {
        this.$message.warning('请输入规则描述');
        return;
      }
      this.generating = true;
      try {
        const config = this.$ai || {};
        const endpoint = config.endpoint || '/v1/chat/completions';
        const model = config.model || 'ernie-4.0';
        const apiKey = config.apiKey || '';

        const headers = { 'Content-Type': 'application/json' };
        if (apiKey) headers['Authorization'] = 'Bearer ' + apiKey;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          signal: AbortSignal.timeout(60000),
          body: JSON.stringify({
            model,
            messages: [
              { role: 'system', content: GENERATE_PROMPT },
              { role: 'user', content: this.userPrompt }
            ],
            stream: false,
            temperature: 0.3
          })
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const result = await res.json();
        const text = result.data || result.choices?.[0]?.message?.content || '';
        // 清理 markdown 代码标记
        const cleaned = text
          .replace(/```(?:javascript|js)?\s*([\s\S]*?)```/g, '$1')
          .replace(/```\s*([\s\S]*?)```/g, '$1')
          .trim();
        if (!cleaned) {
          throw new Error('AI 返回为空');
        }
        this.generatedContent = cleaned;
        this.step = 'preview';
      } catch (e) {
        console.error('AI 生成脚本失败:', e);
        this.$message.error('生成失败: ' + (e.message || ''));
      } finally {
        this.generating = false;
      }
    },

    async regenerateScript() {
      if (!this.userPrompt.trim()) {
        this.$message.warning('请输入规则描述');
        this.step = 'input';
        return;
      }
      await this.generateScript();
    },

    async saveScript() {
      if (!this.scriptName.trim()) {
        this.$message.warning('请输入脚本名称');
        return;
      }
      if (!this.generatedContent) {
        this.$message.warning('请先生成脚本内容');
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.scriptName.trim(),
          description: this.scriptDescription.trim(),
          content: this.generatedContent,
          projectId: this.projectId
        };
        if (this.editScript && this.editScript.id) {
          payload.id = this.editScript.id;
          await updateScanScript(payload);
          this.$message.success('脚本已更新');
        } else {
          await createScanScript(payload);
          this.$message.success('脚本已创建');
        }
        this.$emit('saved', { ...payload, id: this.editScript?.id });
        this.$emit('update:visible', false);
      } catch (e) {
        console.error('保存脚本失败:', e);
        this.$message.error('保存失败: ' + (e.message || ''));
      } finally {
        this.saving = false;
      }
    },

    copyScript() {
      if (!this.generatedContent) return;
      navigator.clipboard.writeText(this.generatedContent).then(() => {
        this.$message.success('已复制到剪贴板');
      }).catch(() => {
        this.$message.warning('复制失败，请手动选择复制');
      });
    }
  }
};
</script>
